import { forwardRef, memo, useState } from 'react';
import type { Target, TargetAndTransition, Transition } from 'framer-motion';
import { AnimatePresence } from 'framer-motion';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import { TABLE_CELL_FULL_ROW_COL_SPAN, TABLE_ROW_EXPANDING_DURATION_MS } from '../../constants.ts';
import {
  ExpandableTableCell,
  ExpandableTableCellMotion,
  ExpandableTableCellMotionInner,
  ExpandableTableRow,
  TableBody,
  TableRowWrapper,
} from './styled.ts';
import type { TableRowProps } from './types.ts';
import { TableRowRole } from './types.ts';

const initial: Target = { height: 0, opacity: 0 };
const animate: TargetAndTransition = { height: 'auto', opacity: 1 };
const exit: TargetAndTransition = { height: 0, opacity: 0 };
const transition: Transition = { duration: TABLE_ROW_EXPANDING_DURATION_MS / 1000, ease: 'easeInOut' };

export const TableRow = memo(
  forwardRef<HTMLTableSectionElement, TableRowProps>(function TableRow(
    { children, className, contents, flash, highlighted, rowRole, onClick, dataTestId },
    ref,
  ) {
    const [isExpanded, setIsExpanded] = useState<boolean>(false);

    const toggleExpandableRow = useCallbackRef(() => {
      setIsExpanded((prev) => !prev);
    });

    return (
      <TableBody
        className={className}
        ref={ref}
        data-is-expanded={isExpanded}
        data-row-role={rowRole ?? TableRowRole.DEFAULT}
        data-testid={dataTestId}>
        <TableRowWrapper
          $isInteractive={!!contents || !!onClick}
          $flash={flash}
          $highlighted={highlighted || isExpanded}
          onClick={contents ? toggleExpandableRow : onClick}
          data-row-role={rowRole ?? TableRowRole.DEFAULT}>
          {children}
        </TableRowWrapper>
        {contents && (
          <AnimatePresence initial={false}>
            {isExpanded && (
              <ExpandableTableRow>
                <ExpandableTableCell colSpan={TABLE_CELL_FULL_ROW_COL_SPAN}>
                  <ExpandableTableCellMotion initial={initial} animate={animate} exit={exit} transition={transition}>
                    <ExpandableTableCellMotionInner>{contents}</ExpandableTableCellMotionInner>
                  </ExpandableTableCellMotion>
                </ExpandableTableCell>
              </ExpandableTableRow>
            )}
          </AnimatePresence>
        )}
      </TableBody>
    );
  }),
);
